'use client';

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Chart } from './Chart';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { theme } from '../../config/theme';
import type { ChartData, ChartLayout, ChartConfig } from '../../types/chart';

const ScatterPlotContainer = styled.div<{ $isFullscreen: boolean }>`
  overflow: visible;
  transition: all 0.3s ease;
  background: transparent;
  
  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    padding: 40px;
    background: ${theme.colors.white};
  `}
`;

const ChartHeader = styled.div`
  padding: 16px 20px 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const ChartTitle = styled.h3`
  display: block;
  margin: 0;
  font-size: 0.75rem;
  font-weight: ${theme.fontWeights.semibold};
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ChartDescription = styled.p`
  display: block;
  margin: 4px 0 0 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

const ChartContent = styled.div`
  padding: ${theme.spacing.lg};
  min-height: 450px;
`;



export interface ScatterPlotSeries {
  name: string;
  x: number[];
  y: number[];
  color?: string;
  size?: number | number[];
  symbol?: string;
}

export interface ScatterPlotData {
  series: ScatterPlotSeries[];
  xAxisLabel?: string;
  yAxisLabel?: string;
}

export interface ScatterPlotProps {
  title: string;
  description?: string;
  data: ScatterPlotData;
  showTrendline?: boolean;
  height?: number;
  className?: string;
}

export const ScatterPlot: React.FC<ScatterPlotProps> = ({
  title,
  description,
  data,
  showTrendline = false,
  height = 450,
  className
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartKey, setChartKey] = useState(0);

  // Monitor fullscreen changes for THIS specific chart only
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!chartRef.current) return;
      
      const doc = document as Document & {
        webkitFullscreenElement?: Element;
        mozFullScreenElement?: Element;
        msFullscreenElement?: Element;
      };
      
      const fullscreenElement = 
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement;
      
      // Only update if THIS chart is the one in fullscreen (or exiting fullscreen)
      const isThisChartFullscreen = fullscreenElement === chartRef.current;
      const wasThisChartFullscreen = isFullscreen;
      
      if (isThisChartFullscreen !== wasThisChartFullscreen) {
        setIsFullscreen(isThisChartFullscreen);
        // Force re-render by changing key
        setChartKey(prev => prev + 1);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  const defaultColors = [
    theme.colors.primary[500],
    theme.colors.success,
    theme.colors.warning,
    theme.colors.error,
    theme.colors.primary[300],
    '#8b5cf6',
  ];


  const chartData: ChartData[] = data.series.map((series, index) => ({
    x: series.x,
    y: series.y,
    type: 'scatter',
    mode: 'markers',
    name: series.name,
    marker: {
      color: series.color || defaultColors[index],
      size: Array.isArray(series.size) ? series.size : (series.size || 10),
      symbol: series.symbol || 'circle',
      line: {
        color: theme.colors.white,
        width: 1,
      },
      opacity: 0.8,
    },
    hoverinfo: 'x+y+name',
    hovertemplate: '<b>%{fullData.name}</b><br>%{x}: %{y}<extra></extra>',
  }));

  // Add trendline if requested
  if (showTrendline && data.series?.length > 0) {
    const series = data.series[0];
    
    // Simple linear regression
    const n = series.x?.length;
    const sumX = series.x.reduce((a, b) => a + b, 0);
    const sumY = series.y.reduce((a, b) => a + b, 0);
    const sumXY = series.x.reduce((sum, xi, i) => sum + xi * series.y[i], 0);
    const sumXX = series.x.reduce((sum, xi) => sum + xi * xi, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    const minX = Math.min(...series.x);
    const maxX = Math.max(...series.x);
    const trendlineX = [minX, maxX];
    const trendlineY = trendlineX.map(x => slope * x + intercept);
    
    chartData.push({
      x: trendlineX,
      y: trendlineY,
      type: 'scatter',
      mode: 'lines',
      name: 'Trendline',
      line: {
        color: theme.colors.error,
        width: 2,
        dash: 'dash',
      },
      hoverinfo: 'skip',
    });
  }

  const layout: ChartLayout = {
    margin: {
      l: 60,
      r: 20,
      t: 10,
      b: 20,
    },
    height,
    hovermode: 'closest',
    xaxis: {
      showgrid: true,
      gridcolor: theme.colors.gray[200],
      title: {
        text: data.xAxisLabel || 'X Axis',
        font: { size: 14, color: theme.colors.gray[700] },
      },
    },
    yaxis: {
      showgrid: true,
      gridcolor: theme.colors.gray[200],
      title: {
        text: data.yAxisLabel || 'Y Axis',
        font: { size: 14, color: theme.colors.gray[700] },
      },
    },
    legend: {
      layout: 'horizontal',
      x: 0.5,
      y: -0.15,
      bgcolor: 'transparent',
      bordercolor: theme.colors.gray[300],
      borderwidth: 1,
    },
  };

  const config: ChartConfig = {
    displayModeBar: false,
    displaylogo: false,
    staticPlot: !zoomEnabled,
    responsive: true,
    scrollZoom: zoomEnabled,
    doubleClick: zoomEnabled ? 'reset' : false,
    modeBarButtonsToRemove: zoomEnabled 
      ? ['lasso2d', 'select2d', 'autoScale2d', 'resetScale2d']
      : ['lasso2d', 'select2d', 'pan2d', 'zoom2d', 'autoScale2d', 'resetScale2d'],
  };



  // Create table data for hamburger menu
  const tableData: Array<Record<string, string | number>> = [];
  data.series.forEach(series => {
    series.x.forEach((xVal, index) => {
      tableData.push({
        series: series.name,
        x: xVal,
        y: series.y[index]
      });
    });
  });


  return (
        <ScatterPlotContainer ref={chartRef} $isFullscreen={isFullscreen} className={className}>
          <ChartHeader>
        <HeaderContent>
              <ChartTitle>{title}</ChartTitle>
              {description && <ChartDescription>{description}</ChartDescription>}
        </HeaderContent>
        <ChartHamburgerMenu 
          chartTitle={title}
          tableData={tableData}
          chartContainerRef={chartRef}
          onZoomToggle={setZoomEnabled}
          initialZoomEnabled={zoomEnabled}
        />
      </ChartHeader>
      
      <ChartContent>
            <Chart
              key={chartKey}
              data={chartData}
              layout={layout}
              config={config}
              revision={chartKey}
            />
      </ChartContent>

    </ScatterPlotContainer>
  );
};
